import { Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MasterdataService } from '@services/masterdata.service';
import { SessionStorageService } from '@services/storage.service';
import { UploadStatus, ValidatorService } from '@shared/models/fileupload';
import { catchError, Subject } from 'rxjs';
import { ISymbolPostRequest, ISymbolPutRequest, SymbolService } from '../symbol.service';

@Injectable({
	providedIn: 'root',
})
export class SymbolvalidatorService implements ValidatorService {
	private statusSubject: Subject<UploadStatus> = new Subject<UploadStatus>();
	public statusSubject$ = this.statusSubject.asObservable();

	constructor(
		private translate: TranslateService,
		private masterdataService: MasterdataService,
		private sessionStorageService: SessionStorageService,
		private symbolService: SymbolService,
	) {}

	postSymbol(fieldData: object, file: File) {
		const request: ISymbolPostRequest = {
			name: fieldData['symbol_name'],
			description: fieldData['symbol_description'],
			width: fieldData['symbol_width'],
			height: fieldData['symbol_height'],
			basePointX: fieldData['symbol_refwidth'],
			basePointY: fieldData['symbol_refheight'],
			fileName: file.name.trim(),
			language: fieldData['symbol_language'],
			isDefault: fieldData['symbol_isDefault'],
		};

		this.symbolService
			.createAndGetUploadUrl(request)
			.pipe(
				catchError((error) => {
					throw new Error(error.error.error.message);
				}),
			)
			.subscribe({
				next: (response) => {
					this.symbolService.uploadFile(response.result.uploadUrl, file).subscribe({
						next: (uploadStatus) => {
							this.statusSubject.next({
								status: 'success',
								data: uploadStatus,
							});
						},
						error: (error: Error) => {
							this.statusSubject.next({ status: 'error', data: error });
						},
					});
				},
				error: (error: Error) => {
					this.statusSubject.next({ status: 'error', data: error.message });
				},
			});
	}

	putSymbol(fieldData: object, context: any, file: File) {
		const request: ISymbolPutRequest = {
			id: context?.name,
			name: fieldData['symbol_name'],
			description: fieldData['symbol_description'],
			width: fieldData['symbol_width'],
			height: fieldData['symbol_height'],
			basePointX: fieldData['symbol_refwidth'],
			basePointY: fieldData['symbol_refheight'],
			fileName: file ? file.name.trim() : undefined,
			language: fieldData['symbol_language'],
			isDefault: fieldData['symbol_isDefault'],
			oldLanguage: context?.language !== fieldData['symbol_language'] ? context?.language || '' : undefined,
		};

		this.symbolService
			.updateAndGetUploadUrl(request, context?.name)
			.pipe(
				catchError((error) => {
					throw new Error(error.error.error.message);
				}),
			)
			.subscribe({
				next: (response) => {
					console.log('Symbolvalidator: putSymbol: response:', response);
					if (response.result.uploadUrl && file) {
						this.symbolService.uploadFile(response.result.uploadUrl, file).subscribe({
							next: (uploadStatus) => {
								this.statusSubject.next({
									status: 'success',
									data: uploadStatus,
								});
							},
							error: (error: Error) => {
								this.statusSubject.next({ status: 'error', data: error });
							},
						});
					} else {
						this.statusSubject.next({
							status: 'success',
							data: 'Data updated.',
						});
					}
				},
				error: (error: Error) => {
					console.error('Symbolvalidator: putSymbol: error:', error);
					this.statusSubject.next({ status: 'error', data: error.message });
				},
			});
	}

	// Validation of file type and/or optional form fields in this function which our ValidatorService interface must implement
	validate(files: File[], fields: FormControl[], context: any) {
		if (files.length) {
			console.log(`Symbolvalidator: Processing file: ${files[0].name}`);
			const formData: FormData = new FormData();
			formData.append('file', files[0]);
		}

		const meta: object = {};
		for (const key in fields) {
			meta[key] = fields[key].getRawValue();
		}
		if (context?.name) {
			this.putSymbol(meta, context, files[0]);
		} else {
			this.postSymbol(meta, files[0]);
		}
	}
}
